import React, { useState, useEffect } from 'react';
import { Table, Dropdown, Menu, Button, Space } from 'antd';
import { EditOutlined, DeleteOutlined, CheckOutlined, RightOutlined, DownOutlined, MoreOutlined } from '@ant-design/icons';
import InfiniteScroll from "react-infinite-scroll-component";
import './styles.css';

const EventsTableComponent = ({
                                  events = [],
                                  languageString = 'ru',
                                  handleDoneEvent,
                                  handleDeleteEvent,
                                  handleRowClick,
                                  loadEvents,
                                  hasMoreEvents,
                                  t = (key) => key,
                                  isLoading = false
                              }) => {
    const [expandedRows, setExpandedRows] = useState([]);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState({ type: '', data: null });
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);

        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);

    const ISSUES_OPTIONS = [
        { label_ru: "Отказ", label_en: "Discard", key: "discard", value: "discard" },
        { label_ru: "Заинтересовало", label_en: "Interest", key: "interest", value: "interest" },
        { label_ru: "Нужно время подумать", label_en: "Need time to think", key: "repeat", value: "repeat" },
        { label_ru: "Потенциально возможно", label_en: "Potentially possible", key: "maybe", value: "maybe" },
        { label_ru: "Предложение принято", label_en: "Offer accepted", key: "submit", value: "submit" }
    ];

    const showConfirmation = (type, data) => {
        setConfirmAction({ type, data });
        setShowConfirmModal(true);
    };

    const handleConfirm = () => {
        const { type, data } = confirmAction;

        switch (type) {
            case 'delete':
                handleDeleteEvent(data);
                break;
            case 'complete':
                handleDoneEvent(data.status, data.id, data.content);
                break;
        }

        setShowConfirmModal(false);
    };

    const handleCancel = () => {
        setShowConfirmModal(false);
        setConfirmAction({ type: '', data: null });
    };

    const handleRowExpand = (record) => {
        const newExpandedRows = expandedRows.includes(record.key)
            ? expandedRows.filter(key => key !== record.key)
            : [...expandedRows, record.key];
        setExpandedRows(newExpandedRows);
    };

    const handleStatusSelect = (status, id, content) => {
        showConfirmation('complete', { status, id, content });
    };

    const canComplete = (statusCode) => {
        return statusCode !== 'done' && statusCode !== 'canceled' && statusCode !== 'delayed';
    };

    const canDelete = (statusCode) => {
        return statusCode !== 'done' && statusCode !== 'canceled' && statusCode !== 'delayed';
    };

    const getConfirmMessage = () => {
        const { type, data } = confirmAction;
        switch (type) {
            case 'delete':
                return t('wantDeleteEvent');
            case 'complete':
                const statusLabel = ISSUES_OPTIONS.find(opt => opt.value === data.status)?.[`label_${languageString}`];
                return `${t('endEventStatus')} "${statusLabel}"?`;
            default:
                return t('sureAction');
        }
    };

    const dataSource = events.map((item, index) => ({
        key: item.id || index,
        id: item.id || index,
        dateTime: item?.on_date?.split('-').reverse().join('.') + ' ' + item?.on_time,
        status: languageString === 'ru' ? item?.event_status?.name?.ru : item?.event_status?.name?.en,
        statusCode: item?.event_status?.code,
        name: item?.name,
        content: item?.add_info,
        employee: languageString === 'ru' ? item?.respondent_ca_user?.user_name : item?.respondent_ca_user?.user_name_en,
        eventOutcome: languageString === 'ru' ? item?.issue?.name?.ru : item?.issue?.name?.en,
        duration: item?.duration,
        eventType: languageString === 'ru' ? item?.event_action?.name?.ru : item?.event_action?.name?.en,
        rawData: item
    }));

    const createStatusDropdown = (record) => {
        const items = ISSUES_OPTIONS.map(option => ({
            key: option.key,
            label: option[`label_${languageString}`],
            onClick: (e) => {
                e.domEvent.stopPropagation();
                handleStatusSelect(option.value, record.id, record.content);
            }
        }));

        return {
            items,
            placement: 'bottomLeft'
        };
    };

    const createMobileActionsDropdown = (record) => {
        const items = [];

        if (canComplete(record.statusCode)) {
            items.push({
                key: 'complete',
                label: (
                    <span style={{ color: '#52c41a' }}>
                        <CheckOutlined style={{ marginRight: '6px' }} />
                        {t('complete')}
                    </span>
                ),
                children: ISSUES_OPTIONS.map(option => ({
                    key: option.key,
                    label: option[`label_${languageString}`],
                    onClick: (e) => {
                        e.domEvent.stopPropagation();
                        handleStatusSelect(option.value, record.id, record.content);
                    }
                }))
            });

            items.push({
                key: 'edit',
                label: (
                    <span style={{ color: '#1890ff' }}>
                        <EditOutlined style={{ marginRight: '6px' }} />
                        {t('edit')}
                    </span>
                ),
                onClick: (e) => {
                    e.domEvent.stopPropagation();
                    handleRowClick(record.rawData);
                }
            });
        }

        if (canDelete(record.statusCode)) {
            items.push({
                key: 'delete',
                label: (
                    <span style={{ color: '#ff4d4f' }}>
                        <DeleteOutlined style={{ marginRight: '6px' }} />
                        {t('delete')}
                    </span>
                ),
                onClick: (e) => {
                    e.domEvent.stopPropagation();
                    showConfirmation('delete', record.id);
                }
            });
        }

        return {
            items,
            placement: 'bottomLeft'
        };
    };

    const desktopColumns = [
        {
            title: t('dateAndTime'),
            dataIndex: 'dateTime',
            key: 'dateTime',
            width: 150,
            sorter: (a, b) => {
                const dateA = new Date(a.dateTime.split(' ')[0].split('.').reverse().join('-') + ' ' + a.dateTime.split(' ')[1]);
                const dateB = new Date(b.dateTime.split(' ')[0].split('.').reverse().join('-') + ' ' + b.dateTime.split(' ')[1]);
                return dateA - dateB;
            },
            showSorterTooltip: false,
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            width: 180,
            sorter: (a, b) => a.status.localeCompare(b.status),
            showSorterTooltip: false,
            render: (text, record) => (
                <span style={{
                    color: record.statusCode === 'done' ? '#52c41a' :
                        record.statusCode === 'canceled' ? '#ff4d4f' :
                            record.statusCode === 'delayed' ? '#faad14' : '#333'
                }}>
                    {text}
                </span>
            )
        },
        {
            title: t('name'),
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
            showSorterTooltip: false,
        },
        {
            title: t('content'),
            dataIndex: 'content',
            key: 'content',
            ellipsis: true,
            sorter: (a, b) => (a.content || '').localeCompare(b.content || ''),
            showSorterTooltip: false,
        },
        {
            title: t('actions'),
            key: 'actions',
            width: 200,
            render: (_, record) => (
                <Space size="small">
                    {canComplete(record.statusCode) && (
                        <Dropdown
                            menu={createStatusDropdown(record)}
                            trigger={['click']}
                        >
                            <Button
                                type="text"
                                size="small"
                                style={{ color: '#52c41a' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                            >
                                <CheckOutlined /> {t('complete')}
                            </Button>
                        </Dropdown>
                    )}
                    {canComplete(record.statusCode) && (
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRowClick(record.rawData);
                            }}
                            style={{ color: '#1890ff' }}
                        />
                    )}
                    {canDelete(record.statusCode) && (
                        <Button
                            type="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                showConfirmation('delete', record.id);
                            }}
                            style={{ color: '#ff4d4f' }}
                        />
                    )}
                </Space>
            )
        }
    ];

    const mobileColumns = [
        {
            title: 'Событие',
            key: 'event',
            render: (_, record) => (
                <div className="mobile-event-card">
                    <div className="mobile-event-header">
                        <div className="mobile-event-date">{record.dateTime}</div>
                        <span className="mobile-event-status" style={{
                            color: record.statusCode === 'done' ? '#52c41a' :
                                record.statusCode === 'canceled' ? '#ff4d4f' :
                                    record.statusCode === 'delayed' ? '#faad14' : '#333'
                        }}>
                            {record.status}
                        </span>
                    </div>
                    {record.name && (
                        <div className="mobile-event-name">{record.name}</div>
                    )}
                    {record.content && (
                        <div className="mobile-event-content">{record.content}</div>
                    )}
                </div>
            )
        },
        {
            title: '',
            key: 'actions',
            width: 50,
            render: (_, record) => (
                canComplete(record.statusCode) || canDelete(record.statusCode) ? (
                    <Dropdown
                        menu={createMobileActionsDropdown(record)}
                        trigger={['click']}
                    >
                        <Button
                            type="text"
                            size="small"
                            icon={<MoreOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                            style={{ color: '#666' }}
                        />
                    </Dropdown>
                ) : null
            )
        }
    ];

    const columns = isMobile ? mobileColumns : desktopColumns;

    const expandedRowRender = (record) => {
        const expandData = [
            { label: 'ID', value: record.id },
            { label: t('dateAndTime'), value: record.dateTime },
            { label: t('status'), value: record.status },
            { label: t('name'), value: record.name },
            { label: t('content'), value: record.content },
            { label: t('employee'), value: record.employee },
            { label: t('eventOutcome'), value: record.eventOutcome },
            { label: t('duration'), value: record.duration },
            { label: t('eventType'), value: record.eventType },
        ].filter(item => item.value);

        return (
            <div style={{ margin: '16px 0', background: '#fafafa', padding: '12px', borderRadius: '4px' }}>
                {expandData.map((item, index) => (
                    <div key={index} style={{ marginBottom: 8, fontSize: '14px', display: 'flex' }}>
                        <strong style={{ minWidth: '140px', color: '#666' }}>{item.label}:</strong>
                        <span style={{ marginLeft: 8, flex: 1, wordWrap: 'break-word' }}>{item.value}</span>
                    </div>
                ))}
            </div>
        );
    };

    const ConfirmModal = () => {
        if (!showConfirmModal) return null;

        return (
            <div className="custom-modal-overlay" onClick={handleCancel}>
                <div className="custom-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="custom-modal-header">
                        <h3>{t('confirmationAction')}</h3>
                        <button className="custom-modal-close" onClick={handleCancel}>×</button>
                    </div>
                    <div className="custom-modal-body">
                        {getConfirmMessage()}
                    </div>
                    <div className="custom-modal-footer">
                        <button className="custom-btn custom-btn-cancel" onClick={handleCancel}>
                            {t('cancel')}
                        </button>
                        <button className="custom-btn custom-btn-confirm" onClick={handleConfirm}>
                            {t('confirm')}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="events-table-container">
            <InfiniteScroll
                dataLength={dataSource.length}
                next={loadEvents}
                hasMore={hasMoreEvents}
                loader={<div style={{ textAlign: 'center', padding: '16px' }}>{t('loading')}</div>}
                endMessage={
                    <div style={{ textAlign: 'center', padding: '16px', color: '#999' }}>
                        {dataSource.length > 0 ? t('dataLoaded') : ''}
                    </div>
                }
                scrollThreshold={0.8}
            >
                <Table
                    dataSource={dataSource}
                    columns={columns}
                    pagination={false}
                    size="small"
                    scroll={isMobile ? { x: 'max-content' } : undefined}
                    rowClassName={(record, index) => {
                        return index % 2 === 0 ? 'table-row-even' : 'table-row-odd';
                    }}
                    expandable={{
                        expandedRowRender,
                        expandedRowKeys: expandedRows,
                        onExpandedRowsChange: setExpandedRows,
                        rowExpandable: () => true,
                        showExpandColumn: false,
                    }}
                    locale={{
                        emptyText: (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#666', fontStyle: 'italic' }}>
                                {t('nothingFound')}
                            </div>
                        )
                    }}
                    className={`custom-events-table ${isMobile ? 'mobile-table' : ''}`}
                    onRow={(record) => ({
                        onClick: (e) => {
                            if (!e.target.closest('.ant-btn') && !e.target.closest('.ant-dropdown-trigger')) {
                                handleRowExpand(record);
                            }
                        },
                        style: { cursor: 'pointer' }
                    })}
                />
            </InfiniteScroll>

            <ConfirmModal />
        </div>
    );
};

export default EventsTableComponent;